import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { Car, Square, Pause, Play, Trash2 } from "lucide-react";
import { useTrip, stopTrip, pauseTrip, resumeTrip, discardTrip } from "@/lib/tripStore";

// In-app floating control shown across the app while a trip is active or
// waiting to be logged — Stop, Pause/Resume, and Discard right here so the
// user never has to navigate back to the dashboard. The system notification
// (background service) is the other indicator, visible when the app is closed.
export function TripStatusBar() {
  const trip = useTrip();
  const [, setLocation] = useLocation();

  if (trip.phase === "idle") return null;
  const tracking = trip.phase === "tracking";
  const paused = trip.paused;

  const statusText = !tracking
    ? "Trip ended — tap to log it"
    : paused
      ? "Paused"
      : trip.error ?? "Tracking trip";

  return (
    <div
      className="fixed inset-x-2 z-50 flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 shadow-lg"
      style={{ bottom: "calc(116px + env(safe-area-inset-bottom, 0px))" }}
    >
      <button
        type="button"
        onClick={() => setLocation("/seller/expenses")}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        aria-label="Open mileage trip"
      >
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Car className="h-4 w-4 text-primary" />
          {tracking && !paused && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
          )}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold tabular-nums">{trip.miles.toFixed(1)} mi</span>
          <span className="block truncate text-[11px] text-muted-foreground">{statusText}</span>
        </span>
      </button>

      {tracking && (
        <>
          {paused ? (
            <IconBtn label="Resume trip" onClick={() => void resumeTrip()} tone="primary">
              <Play className="h-4 w-4" />
            </IconBtn>
          ) : (
            <IconBtn label="Pause trip" onClick={() => void pauseTrip()} tone="muted">
              <Pause className="h-4 w-4" />
            </IconBtn>
          )}
          <IconBtn label="Stop and log trip" onClick={() => void stopTrip(false)} tone="primary">
            <Square className="h-4 w-4" />
          </IconBtn>
        </>
      )}

      <IconBtn label="Discard trip" onClick={() => void discardTrip()} tone="destructive">
        <Trash2 className="h-4 w-4" />
      </IconBtn>
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  tone,
  children,
}: {
  label: string;
  onClick: () => void;
  tone: "primary" | "muted" | "destructive";
  children: ReactNode;
}) {
  const cls =
    tone === "primary"
      ? "bg-primary text-primary-foreground"
      : tone === "destructive"
        ? "bg-destructive/10 text-destructive"
        : "bg-muted text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${cls}`}
    >
      {children}
    </button>
  );
}
